import * as React from 'react';

import { useSupplierMeClient } from '@/api/supplier-me-client-context';
import { useIdentityClient } from '@/api/identity-client-context';
import type { SupplierMeRoleDto } from '@/api/supplier-me-client';
import { displayName } from '@/lib/person-name';
import {
  AuthProvider,
  AuthRefreshProvider,
  anonymousAuthState,
  type AuthRefresh,
  type AuthState,
  type SupplierContactRole,
} from '@/auth/auth-context';
import { isPublicPath } from '@/auth/public-paths';

// =============================================================================
// auth-bootstrap.tsx — Résolution DYNAMIQUE de l'identité de session (L-3, auth admin réelle).
// =============================================================================
// Au montage, résout l'identité de session en TROIS VOIES mutuellement exclusives (cookies distincts) :
//   1. FOURNISSEUR : whoami → { siret } puis me → { role } ⇒ AuthState { role:'supplier', supplierRole }.
//   2. ADMIN (L-3) : sinon getAdminSession → { subject, role:'AdminFull' } ⇒ AuthState { role:'admin',
//      subject } — identité RÉELLE (affichage seul ; tenantId/bearer vides : l'admin est cross-tenant,
//      authentifié par cookie httpOnly, jamais par bearer). REMPLACE l'ancien fallback demoAuthState.
//   3. AUCUNE session : sur un chemin PUBLIC ⇒ anonymousAuthState (la page publique s'affiche, PAS de
//      redirect — anti-loop) ; sur un chemin PROTÉGÉ ⇒ redirect pleine page vers la landing « / »
//      (gate proactif L-3). demoAuthState est SUPPRIMÉ du chemin de PRODUCTION (il ne survit que pour
//      le chemin de test statique #3 de providers.tsx, fixtures admin historiques).
//
// ANTI-LOOP (piège ARCH §3) : AuthBootstrap enveloppe AUSSI la landing (il est au-dessus du router).
// Le redirect pleine page (window.location.assign('/')) n'est émis QUE si le path courant n'est PAS
// public — sinon, sur « / », on rebouclerait à l'infini. La cible « / » étant publique, au rechargement
// resolve() retombe sur anonymousAuthState : boucle rompue par construction. Le prédicat de path public
// est la SOURCE UNIQUE partagée (auth/public-paths.ts), commune au chrome et à l'intercepteur 401.
//
// GATE DE BOOTSTRAP : tant que la résolution n'a pas abouti (loading) ou pendant le redirect
// (redirecting), on NE rend PAS l'arbre role-gated — évite de flasher un mauvais rôle.
//
// Ce composant n'est monté QUE sur le chemin de PRODUCTION (AppProviders bootstrap). Le chemin de test
// injecte un `auth` explicite et court-circuite tout I/O réseau (déterminisme).

/** La session cookie httpOnly est l'autorité ; ce marqueur opaque n'est PAS un secret (RULE_REF_006). */
const SUPPLIER_SESSION_BEARER = 'supplier-session-cookie';

/** Rôle contrat (MASTER|COLLABORATEUR) → SupplierContactRole (mapping explicite, anti-dérive). */
function mapSupplierRole(role: SupplierMeRoleDto): SupplierContactRole {
  return role === 'MASTER' ? 'MASTER' : 'COLLABORATEUR';
}

/**
 * État interne du bootstrap : en cours de résolution, identité figée, ou redirect pleine page en vol
 * (rend le spinner en attendant le rechargement — jamais l'arbre role-gated).
 */
type BootstrapState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly auth: AuthState }
  | { readonly status: 'redirecting' };

export interface AuthBootstrapProps {
  children: React.ReactNode;
}

/**
 * Résout l'identité de session au montage (3 voies : supplier → admin → landing) puis fournit
 * l'AuthState + la capacité `refresh` (re-résolution post-magic-link). Ne monte l'arbre role-gated
 * qu'une fois l'auth résolue (gate anti-flash) ; redirige proactivement une session absente hors
 * chemin public (gate L-3).
 */
export function AuthBootstrap({ children }: AuthBootstrapProps): React.JSX.Element {
  const client = useSupplierMeClient();
  const identity = useIdentityClient();
  const [state, setState] = React.useState<BootstrapState>({ status: 'loading' });

  const resolve = React.useCallback(async (): Promise<void> => {
    // VOIE 1 — session FOURNISSEUR réelle (whoami + me). Inchangé (rétro-compat des tests supplier).
    try {
      const whoami = await client.whoami();
      const me = await client.getMe();
      // P-6 : `me` porte DÉJÀ nom et prénom — le bootstrap ne retenait que le rôle et jetait l'identité,
      // d'où un SIRET affiché à l'utilisateur dans son propre espace. `displayName` rend `null` plutôt
      // qu'une espace quand les deux champs sont vides, sans quoi le repli d'affichage ne serait jamais
      // atteint. Le champ reste OMIS dans ce cas : l'écran décide alors quoi montrer.
      const supplierName = displayName(me.prenom, me.nom);
      setState({
        status: 'ready',
        auth: {
          tenantId: whoami.siret,
          bearer: SUPPLIER_SESSION_BEARER,
          role: 'supplier',
          supplierRole: mapSupplierRole(me.role),
          ...(supplierName !== null ? { name: supplierName } : {}),
          // P-6 : raison sociale de l'entreprise. Omise si l'API rend `null` (aucun relevé
          // d'enrichissement) — l'en-tête retombe alors sur le SIRET.
          ...(me.raisonSociale !== null ? { organisation: me.raisonSociale } : {}),
        },
      });
      return;
    } catch {
      // Pas de session fournisseur (401) ou erreur réseau ⇒ on sonde l'admin.
    }

    // VOIE 2 — session ADMIN réelle (L-3). getAdminSession → { subject } : identité affichage-seul.
    try {
      const session = await identity.getAdminSession();
      setState({
        status: 'ready',
        auth: {
          tenantId: '',
          bearer: '',
          role: 'admin',
          subject: session.subject,
          ...(session.name !== null ? { name: session.name } : {}),
        },
      });
      return;
    } catch {
      // Ni fournisseur ni admin ⇒ voie 3.
    }

    // VOIE 3 — aucune session : anonyme si chemin public (pas de redirect, anti-loop), sinon redirect
    // pleine page vers la landing (gate proactif). window.location.pathname = contexte pré-router.
    if (isPublicPath(window.location.pathname)) {
      setState({ status: 'ready', auth: anonymousAuthState });
    } else {
      setState({ status: 'redirecting' });
      window.location.assign('/');
    }
  }, [client, identity]);

  React.useEffect(() => {
    void resolve();
  }, [resolve]);

  const refreshValue = React.useMemo<AuthRefresh>(
    () => ({ refresh: resolve }),
    [resolve],
  );

  if (state.status !== 'ready') {
    // 'loading' (résolution en cours) ou 'redirecting' (assign('/') en vol) : spinner, jamais l'arbre.
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center text-muted-foreground"
      >
        <span>Chargement de votre session…</span>
      </div>
    );
  }

  return (
    <AuthProvider value={state.auth}>
      <AuthRefreshProvider value={refreshValue}>{children}</AuthRefreshProvider>
    </AuthProvider>
  );
}
